import React, { FC, useEffect, useMemo, useState } from "react";

import { css } from "@emotion/react";
import {
  AnyProgressBarProps,
  AnyProgressBarWidgetTheme,
  DiscountProgressBarProgress,
  WidgetPrefixes,
} from "@intelligems/ig-types";
import { ThemeProvider } from "@mui/material";

import { DiscountProgressBarContext } from "../context";
import { FullIgTheme, IgWidgetTheme } from "../theme";

import { Bar } from "./Bar";
import { RowBuilder } from "./RowBuilder";

export const AnyProgressBarWidgetBase: FC<AnyProgressBarProps> = ({
  prefix,
  type,
  handleMessage,
  windowState,
  isMobile,
  getWidgetConfig,
  currencyFormatter,
  config,
}) => {
  const [isInit, setIsInit] = useState<boolean>(true);

  const prefixes: WidgetPrefixes | undefined = useMemo(() => {
    if (prefix) {
      return {
        "ig-pb": prefix,
      };
    }
    return undefined;
  }, [prefix]);

  const getTheme = () => {
    if (isMobile) {
      return IgWidgetTheme(
        config.mobile?.variant ||
          config.desktop?.variant ||
          "progressBarWidget1",
        true,
        prefixes
      ) as AnyProgressBarWidgetTheme;
    }
    return IgWidgetTheme(
      config.desktop?.variant || "progressBarWidget1",
      false,
      prefixes
    ) as AnyProgressBarWidgetTheme;
  };

  const [theme, setTheme] = useState(getTheme());

  const [delayedProgress, setDelayedProgress] =
    useState<DiscountProgressBarProgress>(windowState.progress);

  useEffect(() => {
    let time;
    if (windowState.progress.progress < delayedProgress.progress) {
      time = 0;
    } else if (isInit && windowState.progress > delayedProgress) {
      if (isInit) {
        setIsInit(false);
      }
      time = 0;
    } else {
      time = 700;
    }
    const timer = setTimeout(() => {
      setDelayedProgress(windowState.progress);
    }, time);

    return () => clearTimeout(timer);
  }, [windowState.progress]);

  useEffect(() => {
    setTheme(getTheme());
  }, [prefixes, config.desktop?.variant, config.mobile?.variant, isMobile]);

  const containerClass = useMemo(
    () => css`
      display: flex;
      flex-direction: column;
      align-items: center;
      min-height: ${theme.container.minHeight
        ? `${theme.container.minHeight}px`
        : undefined};
      max-height: ${theme.container.maxHeight
        ? `${theme.container.maxHeight}px`
        : undefined};
      justify-content: ${theme.container.justifyContent};
      flex-wrap: nowrap;

      font-family: ${theme.container.font.family};
      margin: ${theme.container.margin};
      background-color: ${theme.container.backgroundColor};
      padding: ${theme.container.padding};

      @media only screen and (max-width: 600px) {
        min-width: 350px;
        width: 350px;
        max-width: 350px;
      }
      @media only screen and (min-width: 601px) {
        min-width: 350px;
        width: 350px;
        max-width: 350px;
      }
    `,
    [theme]
  );

  const mainContentClass = useMemo(
    () => css`
      display: flex;
      width: 100%;
      align-items: center;
      position: relative;
      justify-content: start;
      margin: ${windowState.discount.tiers.length === 2
        ? theme.breakpoint.margin
        : 0}px;
      padding: ${theme.breakpoint.padding};
      height: ${theme.breakpoint.height}px;
    `,
    [theme]
  );

  const rowClass = useMemo(
    () => css`
      width: 100%;
      display: flex;
    `,
    [theme]
  );

  const row1Class = useMemo(
    () => css`
      ${rowClass};
      justify-content: ${theme.row1.justifyContent};
      //margin-top: 20px;
    `,
    [theme.row1.justifyContent]
  );

  const row2Class = useMemo(
    () => css`
      ${rowClass};
      justify-content: ${theme.row2.justifyContent};
    `,
    [theme.row2.justifyContent]
  );

  const row3Class = useMemo(
    () => css`
      ${rowClass};
      justify-content: ${theme.row3.justifyContent};
    `,
    [theme.row3.justifyContent]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, []);

  const widgetCurrencyFormatter = useMemo(() => {
    if (currencyFormatter) {
      return currencyFormatter;
    }
    const formatter = new Intl.NumberFormat("en-us", {
      maximumFractionDigits: 0,
      currency: "USD",
      style: "currency",
    });

    return (input: number) => {
      return formatter.format(input);
    };
  }, [currencyFormatter]);

  return (
    <ThemeProvider theme={FullIgTheme(isMobile, prefixes)}>
      <DiscountProgressBarContext.Provider
        value={{
          prefixes,
          config,
          windowState,
          theme,
          isMobile,
          currencyFormatter: widgetCurrencyFormatter,
          getWidgetConfig,
        }}
      >
        <div className={`ig-dp-container`} css={containerClass}>
          {windowState.message === undefined ||
          windowState.unlockedMessage === undefined ? null : (
            <>
              <div className={"ig-dp-row-1"} css={row1Class}>
                <RowBuilder row={1} />
              </div>
              <div className={"ig-dp-row-2"} css={row2Class}>
                <div className={"ig-dp-row-container"} css={mainContentClass}>
                  <Bar init={isInit} />
                  <RowBuilder row={2} />
                </div>
              </div>
              <div className={"ig-dp-row-3"} css={row3Class}>
                <RowBuilder row={3} />
              </div>
            </>
          )}
        </div>
      </DiscountProgressBarContext.Provider>
    </ThemeProvider>
  );
};
